import { supabase } from './supabase';
import {
    Lead,
    LeadColor,
    LeadEvent,
    LeadReminder,
    LeadStage,
    DueLeadReminder,
} from '../types/leads.types';

export interface LeadFilters {
    source?: string;
    assignedTo?: string;
    search?: string;
}

// Finestra oltre la quale un reminder scaduto non genera più notifica: resta
// nella pagina Lead, ma non riappare come allarme a ogni apertura dell'app.
export const DUE_REMINDER_MAX_AGE_MS = 12 * 60 * 60 * 1000;
// Tetto difensivo sulla query: se ne sono scaduti tanti non ha senso caricarli tutti.
const DUE_REMINDER_QUERY_LIMIT = 500;

export const leadService = {
    async getLeads(filters: LeadFilters = {}): Promise<Lead[]> {
        let query = supabase
            .from('leads' as any)
            .select(`
                *,
                assignee:profiles!assigned_to(id, full_name, email)
            `)
            .order('created_at', { ascending: false });

        if (filters.source) query = query.eq('source', filters.source);
        if (filters.assignedTo) query = query.eq('assigned_to', filters.assignedTo);

        const { data, error } = await query;
        if (error) throw error;

        let leads = (data || []) as unknown as Lead[];

        if (filters.search) {
            const term = filters.search.toLowerCase();
            leads = leads.filter((l) =>
                [l.full_name, l.company_name, l.email, l.phone]
                    .filter(Boolean)
                    .some((v) => (v as string).toLowerCase().includes(term))
            );
        }

        return leads;
    },

    // Lead creati dopo un certo istante (ISO). Alimenta l'allarme "nuovo lead"
    // in arrivo (polling client-side). Ordine crescente per elaborarli in ordine.
    async getLeadsCreatedAfter(sinceIso: string): Promise<Lead[]> {
        const { data, error } = await supabase
            .from('leads' as any)
            .select('id, full_name, company_name, source, form_name, created_at, created_by')
            .gt('created_at', sinceIso)
            .order('created_at', { ascending: true });
        if (error) throw error;
        return (data || []) as unknown as Lead[];
    },

    async getLead(id: string): Promise<Lead> {
        const { data, error } = await supabase
            .from('leads' as any)
            .select(`
                *,
                assignee:profiles!assigned_to(id, full_name, email)
            `)
            .eq('id', id)
            .single();

        if (error) throw error;
        return data as unknown as Lead;
    },

    async createLead(
        lead: Partial<Omit<Lead, 'id' | 'created_at' | 'updated_at'>>
    ): Promise<Lead> {
        const { data: userData } = await supabase.auth.getUser();

        const payload = {
            ...lead,
            source: lead.source ?? 'personale',
            created_by: userData.user?.id ?? null,
        };

        const { data, error } = await supabase
            .from('leads' as any)
            .insert([payload])
            .select()
            .single();

        if (error) throw error;
        return data as unknown as Lead;
    },

    async updateLead(id: string, updates: Partial<Lead>): Promise<Lead> {
        // rimuovo eventuali join non-colonna prima dell'update
        const { assignee, reminders, ...clean } = updates as any;
        const { data, error } = await supabase
            .from('leads' as any)
            .update(clean)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data as unknown as Lead;
    },

    /** Etichetta colore della card (null = nessun colore). */
    async setColor(id: string, color: LeadColor | null): Promise<void> {
        const { error } = await supabase
            .from('leads' as any)
            .update({ color })
            .eq('id', id);

        if (error) throw error;
    },

    async deleteLead(id: string): Promise<void> {
        const { error } = await supabase
            .from('leads' as any)
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    /**
     * Aggiorna lo stage e registra un evento stage_change nella timeline.
     * `patch` consente di scrivere atomicamente campi correlati (es. lost_reason
     * quando si passa a 'perso', o azzerare la prossima azione).
     */
    async updateStage(
        id: string,
        stage: LeadStage,
        prevStage?: LeadStage,
        patch: Partial<Pick<Lead, 'lost_reason' | 'next_action' | 'next_action_at'>> = {}
    ): Promise<void> {
        const { error } = await supabase
            .from('leads' as any)
            .update({ stage, ...patch })
            .eq('id', id);

        if (error) throw error;

        // In Vinto il prospect collegato diventa cliente attivo
        if (stage === 'vinto') {
            try {
                const { data: leadRow } = await supabase
                    .from('leads' as any)
                    .select('converted_customer_id')
                    .eq('id', id)
                    .maybeSingle();
                const customerId = (leadRow as any)?.converted_customer_id as string | null;
                if (customerId) {
                    await supabase
                        .from('customers')
                        .update({ status: 'active' } as any)
                        .eq('id', customerId);
                }
            } catch {
                // promozione cliente non bloccante
            }
        }

        try {
            await this.createEvent({
                lead_id: id,
                event_type: 'stage_change',
                title: prevStage
                    ? `Stato: ${prevStage} → ${stage}`
                    : `Stato aggiornato a ${stage}`,
                notes: null,
                event_date: new Date().toISOString(),
                status: 'done',
            });
        } catch {
            // il log non deve bloccare l'update dello stage
        }
    },

    // --- Eventi (timeline) -----------------------------------------------

    async getEvents(leadId: string): Promise<LeadEvent[]> {
        const { data, error } = await supabase
            .from('lead_events' as any)
            .select(`
                *,
                reminders:lead_reminders(*),
                creator:profiles!created_by(id, full_name, email)
            `)
            .eq('lead_id', leadId)
            .order('event_date', { ascending: false });

        if (error) throw error;
        return (data || []) as unknown as LeadEvent[];
    },

    async createEvent(
        event: Omit<LeadEvent, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'reminders' | 'creator'>
    ): Promise<LeadEvent> {
        const { data: userData } = await supabase.auth.getUser();

        const payload = { ...event, created_by: userData.user?.id ?? null };

        const { data, error } = await supabase
            .from('lead_events' as any)
            .insert([payload])
            .select()
            .single();

        if (error) throw error;
        return data as unknown as LeadEvent;
    },

    /** Solo gli admin possono eliminare (policy RLS su lead_events). */
    async deleteEvent(id: string): Promise<void> {
        // `select()` ci restituisce le righe davvero cancellate: senza permessi
        // la RLS non filtra con un errore, semplicemente non tocca nulla.
        const { data, error } = await supabase
            .from('lead_events' as any)
            .delete()
            .eq('id', id)
            .select('id');

        if (error) throw error;
        if (!data || data.length === 0) {
            throw new Error('Attività non eliminata: servono i permessi di amministratore.');
        }
    },

    /**
     * Azione rapida "1-click": registra un contatto avvenuto (status 'done', ora
     * corrente). Il trigger DB aggiorna last_event_at -> azzera il contatore
     * "giorni fermo". Ritorna l'evento creato.
     */
    async logActivity(
        leadId: string,
        eventType: LeadEvent['event_type'],
        title: string,
        notes: string | null = null
    ): Promise<LeadEvent> {
        return this.createEvent({
            lead_id: leadId,
            event_type: eventType,
            title,
            notes,
            event_date: new Date().toISOString(),
            status: 'done',
        });
    },

    // --- Reminder ---------------------------------------------------------

    async createReminder(
        reminder: Omit<LeadReminder, 'id' | 'created_at' | 'created_by'>
    ): Promise<LeadReminder> {
        const { data: userData } = await supabase.auth.getUser();

        const payload = { ...reminder, created_by: userData.user?.id ?? null };

        const { data, error } = await supabase
            .from('lead_reminders' as any)
            .insert([payload])
            .select()
            .single();

        if (error) throw error;
        return data as unknown as LeadReminder;
    },

    async completeReminder(id: string): Promise<void> {
        const { error } = await supabase
            .from('lead_reminders' as any)
            .update({ is_completed: true })
            .eq('id', id);

        if (error) throw error;
    },

    /** Reminder non completati e già scaduti (o entro `withinMs`), con dati lead.
     *  `maxAgeMs` scarta quelli scaduti da troppo tempo: serve alle notifiche, che non
     *  devono ripresentare come muro all'apertura dell'app arretrati di settimane.
     *  Passare `Infinity` per prenderli tutti (badge "da ricontattare" nella pagina Lead). */
    async getDueReminders(withinMs = 60_000, maxAgeMs = DUE_REMINDER_MAX_AGE_MS): Promise<DueLeadReminder[]> {
        const now = Date.now();
        const threshold = new Date(now + withinMs).toISOString();

        let query = supabase
            .from('lead_reminders' as any)
            .select(`
                *,
                event:lead_events(
                    lead_id,
                    title,
                    lead:leads(full_name, company_name)
                )
            `)
            .eq('is_completed', false)
            .lte('remind_at', threshold);

        if (Number.isFinite(maxAgeMs)) {
            query = query.gte('remind_at', new Date(now - maxAgeMs).toISOString());
        }

        const { data, error } = await query
            .order('remind_at', { ascending: true })
            .limit(DUE_REMINDER_QUERY_LIMIT);

        if (error) throw error;
        return (data || []) as unknown as DueLeadReminder[];
    },

    // --- Log email inviata come evento ----------------------------------

    async logEmailSent(leadId: string, subject: string, body: string): Promise<void> {
        await this.createEvent({
            lead_id: leadId,
            event_type: 'email',
            title: subject,
            notes: body,
            event_date: new Date().toISOString(),
            status: 'done',
        });
    },

    // --- Conversione lead -> cliente ------------------------------------

    /** Crea un cliente dai dati del lead e collega il lead. Ritorna l'id del cliente.
     *  Se esiste già un prospect (es. creato in Proposta), lo promuove a cliente attivo e passa a Vinto. */
    async convertToCustomer(lead: Lead): Promise<string> {
        const { data: userData } = await supabase.auth.getUser();
        let customerId = lead.converted_customer_id;

        if (customerId) {
            const { error: promoteErr } = await supabase
                .from('customers')
                .update({ status: 'active' } as any)
                .eq('id', customerId);
            if (promoteErr) throw promoteErr;
        } else {
            const customerPayload = {
                company_name: lead.company_name || lead.full_name || 'Nuovo cliente',
                contact_person: lead.full_name || null,
                email: lead.email || null,
                phone: lead.phone || null,
                city: lead.city || null,
                province: lead.province || null,
                notes: `Convertito da lead (${lead.source})${lead.notes ? `\n\n${lead.notes}` : ''}`,
                status: 'active',
                created_by: userData.user?.id,
            };

            const { data: customer, error: custErr } = await supabase
                .from('customers')
                .insert([customerPayload as any])
                .select('id')
                .single();

            if (custErr) throw custErr;
            customerId = (customer as any).id as string;
        }

        const { error: updErr } = await supabase
            .from('leads' as any)
            .update({ converted_customer_id: customerId, stage: 'vinto' })
            .eq('id', lead.id);

        if (updErr) throw updErr;

        try {
            await this.createEvent({
                lead_id: lead.id,
                event_type: 'conversione',
                title: 'Lead convertito in cliente',
                notes: null,
                event_date: new Date().toISOString(),
                status: 'done',
            });
        } catch {
            // log non bloccante
        }

        return customerId;
    },

    /**
     * Da lead qualificato: crea (se serve) un cliente prospect, passa lo stage a
     * "proposta", crea un'offerta bozza a nome suo e ritorna gli id per aprirla in modifica.
     */
    async createOfferFromLead(lead: Lead): Promise<{ customerId: string; priceListId: string }> {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id;
        if (!userId) throw new Error('Utente non autenticato');

        let customerId = lead.converted_customer_id;

        if (!customerId) {
            const customerPayload = {
                company_name: lead.company_name || lead.full_name || 'Nuovo cliente',
                contact_person: lead.full_name || null,
                email: lead.email || null,
                phone: lead.phone || null,
                city: lead.city || null,
                province: lead.province || null,
                notes: `Prospect da lead (${lead.source})${lead.notes ? `\n\n${lead.notes}` : ''}`,
                status: 'prospect',
                created_by: userId,
            };

            const { data: customer, error: custErr } = await supabase
                .from('customers')
                .insert([customerPayload as any])
                .select('id')
                .single();

            if (custErr) throw custErr;
            customerId = (customer as any).id as string;
        }

        const prevStage = lead.stage;
        const { error: updErr } = await supabase
            .from('leads' as any)
            .update({
                converted_customer_id: customerId,
                stage: 'proposta',
            })
            .eq('id', lead.id);

        if (updErr) throw updErr;

        const clientLabel = lead.company_name || lead.full_name || 'Cliente';
        const offerName = `Offerta ${clientLabel}`.trim();

        const { data: priceList, error: plErr } = await supabase
            .from('price_lists')
            .insert([{
                name: offerName,
                description: `Offerta generata da lead qualificato`,
                customer_id: customerId,
                created_by: userId,
                currency: 'EUR',
                status: 'draft',
                is_active: true,
                is_default: false,
                print_conditions: true,
                print_discount: true,
                print_attachments: true,
            } as any])
            .select('id')
            .single();

        if (plErr) throw plErr;
        const priceListId = (priceList as any).id as string;

        try {
            if (prevStage !== 'proposta') {
                await this.createEvent({
                    lead_id: lead.id,
                    event_type: 'stage_change',
                    title: `Stato: ${prevStage} → proposta`,
                    notes: 'Generata offerta da lead qualificato',
                    event_date: new Date().toISOString(),
                    status: 'done',
                });
            }
            await this.createEvent({
                lead_id: lead.id,
                event_type: 'note',
                title: 'Offerta creata',
                notes: offerName,
                event_date: new Date().toISOString(),
                status: 'done',
            });
        } catch {
            // log non bloccante
        }

        return { customerId, priceListId };
    },

    // --- Profili per assegnazione ---------------------------------------

    async getAssignableProfiles(): Promise<{ id: string; full_name: string | null; email: string }[]> {
        const { data, error } = await supabase
            .from('profiles')
            .select('id, full_name, email, role')
            .in('role', ['admin', 'commerciale', 'sales', 'amministrazione']);

        if (error) throw error;
        return (data || []) as unknown as { id: string; full_name: string | null; email: string }[];
    },
};
